import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditIcon from "@mui/icons-material/Edit";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import { Box, Button, Card, Typography } from "@mui/material";
import { useMemo } from "react";
import { AppColors } from "../../../../../util/AppColors";
import {
  getWorkbookItemActualCost,
  getWorkbookItemDisplayCost,
} from "../workbookInventoryHelpers";

const AdministerCard = ({
  item,
  onEdit,
  onDelete,
  onToggleAdministered,
  isToggling = false,
}) => {
  const mandateDataRaw = sessionStorage.getItem("mandateData");
  const { clinicStocks, clinicId, locations } = useMemo(() => {
    if (!mandateDataRaw)
      return { clinicStocks: [], clinicId: null, locations: [] };
    try {
      const reqData = JSON.parse(mandateDataRaw);
      return {
        clinicStocks: Array.isArray(reqData?.stocks) ? reqData.stocks : [],
        clinicId: reqData?.clinic?.id ?? null,
        locations: Array.isArray(reqData?.locations) ? reqData.locations : [],
      };
    } catch {
      return { clinicStocks: [], clinicId: null, locations: [] };
    }
  }, [mandateDataRaw]);

  const reqStock = useMemo(() => {
    if (!item?.stockId) return null;
    return clinicStocks?.find((stc) => stc?.stockId === item?.stockId) ?? null;
  }, [item?.stockId, clinicStocks]);

  const batchNo = useMemo(() => {
    if (item?.batchNo) return item.batchNo;
    if (!item?.batchId || !reqStock) return null;

    const reqBatch = reqStock?.stockBatch
      ?.filter((btch) => btch?.createdBy === clinicId)
      ?.find((bat) => bat?.batchId === item?.batchId);

    return reqBatch?.batchNo ?? null;
  }, [item, reqStock, clinicId]);

  const isControlledSubstance = useMemo(() => {
    if (!reqStock) return false;
    const isScheduleH1 =
      (reqStock?.drugSchedule ?? "")?.toLowerCase() === "schedule h1";
    const isNdps =
      reqStock?.ndps === true ||
      reqStock?.ndps === "Y" ||
      reqStock?.ndps === "y";
    return isScheduleH1 || isNdps;
  }, [reqStock]);

  const isRequested = item?.status === "requested";

  const isRejected =
    item?.status === "rejected" ||
    item?.isRejected ||
    item?.remarks === "Rejected";

  const isStockUnavailable =
    item?.status === "rejected" && item?.remarks === "Stock Unavailable";

  const isAdministered =
    !isRequested &&
    !isRejected &&
    (item?.status === true ||
      item?.status === "true" ||
      item?.remarks === "Completed" ||
      item?.billingStatus === "Administered");

  const getDotColor = (status) => {
    if (status === "green") return "#4caf50";
    if (status === "yellow") return "#ff9800";
    if (status === "red") return "#f44336";
    if (isRequested) return "#ff9800";
    if (isStockUnavailable) return "#ff9800";
    if (isRejected) return "#f44336";
    if (isAdministered) return "#4caf50";
    return "#4caf50";
  };

  const selectedBatch = useMemo(() => {
    if (!reqStock) return null;
    const batchId = item?.batchId ?? item?.batchNumber?.value ?? null;
    if (batchId) {
      return (
        reqStock?.stockBatch?.find(
          (bat) => String(bat?.batchId) === String(batchId),
        ) ?? null
      );
    }
    return (
      reqStock?.stockBatch?.find((btch) => btch?.createdBy === clinicId) ??
      reqStock?.stockBatch?.[0] ??
      null
    );
  }, [reqStock, item?.batchId, item?.batchNumber?.value, clinicId]);

  const locationName = useMemo(() => {
    if (item?.batchNumber?.locationName) {
      return item.batchNumber.locationName;
    }
    if (!selectedBatch?.locationId) return null;
    return (
      locations?.find((loc) => loc?.id === selectedBatch?.locationId)
        ?.locationName ?? null
    );
  }, [item, selectedBatch, locations]);

  const quantity = Number(item?.quantity ?? 0);
  const uomName =
    item?.uomName ||
    selectedBatch?.salesUom ||
    selectedBatch?.qtyMapName ||
    selectedBatch?.uomMapName ||
    "";
  const actualCost = useMemo(
    () =>
      getWorkbookItemActualCost({
        item,
        selectedBatch,
        quantity,
      }),
    [item, selectedBatch, quantity],
  );
  const displayCost = getWorkbookItemDisplayCost(item, actualCost);

  return (
    <Card
      sx={{
        borderRadius: "8px",
        boxShadow: "0px 1px 4px rgba(0, 0, 0, 0.06)",
        border: "1px solid #e0e0e0",
        p: 2,
        mb: 1.5,
        backgroundColor: "#ffffff",
      }}
    >
      {/* Top Main Row */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={1.5}
      >
        {/* Left: Dot & Name */}
        <Box display="flex" alignItems="center" gap={1.2}>
          <Box
            sx={{
              width: 9,
              height: 9,
              borderRadius: "50%",
              backgroundColor: getDotColor(item?.status),
              flexShrink: 0,
            }}
          />
          <Typography
            sx={{
              fontSize: "15px",
              fontWeight: 700,
              color: "#212121",
            }}
          >
            {item?.procedureName}
          </Typography>
        </Box>

        {/* Right: Qty/Price, Status, Edit & Delete */}
        <Box display="flex" alignItems="center" gap={1.2}>
          <Typography
            sx={{
              fontSize: "13px",
              color: "#616161",
              fontWeight: 500,
            }}
          >
            {quantity} {uomName} · ₹{Number(displayCost).toFixed(2)}
          </Typography>

          {isRequested ? (
            <Box
              sx={{
                backgroundColor: "#fff3e0",
                color: "#e65100",
                fontSize: "12px",
                fontWeight: 600,
                px: 1.2,
                py: 0.3,
                borderRadius: "4px",
                border: "1px solid #ffe082",
              }}
            >
              Requested
            </Box>
          ) : isRejected ? (
            <Typography
              className={`${
                isStockUnavailable ? "orange-back2" : "red-back2"
              } white-color status-con fs12 font-medium con-fit`}
            >
              {isStockUnavailable ? "Stock Unavailable" : "Rejected"}
            </Typography>
          ) : (
            <>
              <Box
                sx={{
                  backgroundColor: isAdministered ? "#e8f5e9" : "#e0f2fe",
                  color: isAdministered ? "#2e7d32" : "#0284c7",
                  fontSize: "12px",
                  fontWeight: 600,
                  px: 1.2,
                  py: 0.3,
                  borderRadius: "4px",
                  border: isAdministered
                    ? "1px solid #c8e6c9"
                    : "1px solid #bae6fd",
                }}
              >
                {isAdministered ? "Administered" : "Issued"}
              </Box>

              {!isAdministered && (
                <Button
                  size="small"
                  variant="contained"
                  disabled={isToggling}
                  onClick={() =>
                    onToggleAdministered && onToggleAdministered(item)
                  }
                  sx={{
                    backgroundColor: AppColors.appPrimary,
                    color: "#fff",
                    textTransform: "none",
                    fontWeight: 600,
                    fontSize: "12px",
                    height: "30px",
                    px: 1.5,
                    boxShadow: "none",
                    whiteSpace: "nowrap",
                    "&:hover": {
                      backgroundColor: "#002b58",
                      boxShadow: "none",
                    },
                    "&.Mui-disabled": {
                      backgroundColor: AppColors.appPrimary,
                      color: "#fff",
                      opacity: 0.6,
                    },
                  }}
                >
                  Mark Administered
                </Button>
              )}
            </>
          )}
          <Button
            startIcon={<EditIcon sx={{ fontSize: 16 }} />}
            onClick={() => onEdit(item)}
            sx={{
              color: AppColors.appPrimary,
              textTransform: "none",
              fontWeight: 600,
              fontSize: "13px",
              minWidth: "auto",
              p: "4px 8px",
              whiteSpace: "nowrap",
            }}
          >
            Edit
          </Button>
          {item?.billing &&
          ["true", "issued"].includes(
            (item?.status ?? "")?.toLowerCase(),
          ) ? null : (
            <Button
              startIcon={<DeleteOutlineIcon sx={{ fontSize: 18 }} />}
              onClick={() => onDelete(item?.detailId ?? item?.id)}
              sx={{
                color: "#d32f2f",
                textTransform: "none",
                fontWeight: 600,
                fontSize: "13px",
                minWidth: "auto",
                p: "4px 8px",
                whiteSpace: "nowrap",
                "&:hover": {
                  backgroundColor: "rgba(211, 47, 47, 0.04)",
                },
              }}
            >
              Delete
            </Button>
          )}
        </Box>
      </Box>

      {/* Sub-row: Badges */}
      <Box display="flex" alignItems="center" flexWrap="wrap" gap={1}>
        {batchNo ? (
          <Box
            sx={{
              backgroundColor: "#f5f5f5",
              color: "#616161",
              fontSize: "11px",
              fontWeight: 500,
              px: 1.2,
              py: 0.4,
              borderRadius: "4px",
              border: "1px solid #e0e0e0",
            }}
          >
            Batch: {batchNo}
          </Box>
        ) : null}

        {locationName ? (
          <Box
            sx={{
              backgroundColor: "#f5f5f5",
              color: "#616161",
              fontSize: "11px",
              fontWeight: 500,
              px: 1.2,
              py: 0.4,
              borderRadius: "4px",
              border: "1px solid #e0e0e0",
            }}
          >
            Location: {locationName}
          </Box>
        ) : null}

        {item?.dosage ? (
          <Box
            sx={{
              backgroundColor: "#f5f5f5",
              color: "#616161",
              fontSize: "11px",
              fontWeight: 500,
              px: 1.2,
              py: 0.4,
              borderRadius: "4px",
              border: "1px solid #e0e0e0",
            }}
          >
            Dose: {item.dosage}
            {selectedBatch?.packageUom ? ` ${selectedBatch.packageUom}` : ""}
          </Box>
        ) : null}

        {item?.route ? (
          <Box
            sx={{
              backgroundColor: "#f5f5f5",
              color: "#616161",
              fontSize: "11px",
              fontWeight: 500,
              px: 1.2,
              py: 0.4,
              borderRadius: "4px",
              border: "1px solid #e0e0e0",
            }}
          >
            Route: {item.route}
          </Box>
        ) : null}

        {item?.billSeparated ? (
          <Box
            sx={{
              backgroundColor: "#fff8e1",
              color: "#e65100",
              fontSize: "12px",
              fontWeight: 600,
              px: 1.2,
              py: 0.4,
              borderRadius: "4px",
              border: "1px solid #ffe082",
            }}
          >
            {`+ ₹${Number(actualCost).toFixed(2)} billed separately (Clinic)`}
          </Box>
        ) : null}

        {item?.billing ? (
          <Box
            sx={{
              backgroundColor: "#e8eaf6",
              color: "#283593",
              fontSize: "12px",
              fontWeight: 600,
              px: 1.2,
              py: 0.4,
              borderRadius: "4px",
              border: "1px solid #c5cae9",
              display: "flex",
              alignItems: "center",
              gap: 0.5,
            }}
          >
            <span>📦</span> Requested from Pharmacy
          </Box>
        ) : null}

        <Box
          sx={{
            backgroundColor: item?.linked === "Y" ? "#f5f5f5" : "#e3f2fd",
            color: item?.linked === "Y" ? "#616161" : AppColors.appPrimary,
            fontSize: "12px",
            fontWeight: 600,
            px: 1.2,
            py: 0.4,
            borderRadius: item?.linked === "Y" ? "4px" : "12px",
            border: item?.linked === "Y" ? "1px solid #e0e0e0" : "none",
            display: "flex",
            alignItems: "center",
            gap: 0.5,
          }}
        >
          {item?.linked !== "Y" && <LocalOfferIcon sx={{ fontSize: 13 }} />}
          {item?.linked !== "Y" ? "Not linked" : item?.procedure?.procedureName}
        </Box>
        {isControlledSubstance ? (
          <Box
            sx={{
              backgroundColor: "#ffebee",
              color: "#d32f2f",
              fontSize: "12px",
              fontWeight: 600,
              px: 1.2,
              py: 0.3,
              borderRadius: "4px",
              border: "1px solid #ffcdd2",
            }}
          >
            Controlled substance
          </Box>
        ) : null}
      </Box>
    </Card>
  );
};

export default AdministerCard;
